import axios from "axios";
import { DataTable } from "simple-datatables";
import { handleRequestError, showAlert } from "../utils/alert";
import flatpickr from "flatpickr";
import "flatpickr/dist/themes/light.css";
import { Portuguese } from "flatpickr/dist/l10n/pt.js";

export default () => ({
  // Authorization State
  hasAccessModule: true,

  reportList: [],
  hasSearched: false,
  summary: {
    total_income: 0,
    total_expense: 0,
    net_balance: 0,
  },
  dataTableInstance: null,

  filters: {
    start_date: "",
    end_date: "",
  },

  fpStartDate: null,
  fpEndDate: null,

  async initCashFlow() {
    const token = localStorage.getItem("jwt_token");
    if (!token) return (window.location.href = "/client/login");
    window.axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    try {
      // "Ping" na API para testar o acesso do usuário.
      try {
        await axios.get("/api/client/reports/cash-flow");
      } catch (pingError) {
        // Se o middleware do Laravel barrar, ele lança um 403 e nós jogamos para o catch principal
        if (pingError.response && pingError.response.status === 403) {
          throw pingError;
        }
        // Ignoramos outros erros (como 422 Unprocessable Entity, porque não enviamos as datas).
        // Isso significa que o usuário passou no controle de acesso.
      }

      // Se não caiu no 403 acima, o usuário tem acesso normal. Iniciamos o formulário.
      this.initFlatpickr();
    } catch (error) {
      // Captura o erro 403 do ping e bloqueia a view
      if (error.response && error.response.status === 403) {
        this.hasAccessModule = false;
        return;
      }

      // Ignora se for problema de autenticação/pagamento
      if (error.response && [401, 402].includes(error.response.status)) return;

      console.error("Infrastructure error:", error);
    }
  },

  initFlatpickr() {
    const config = {
      locale: Portuguese,
      dateFormat: "Y-m-d",
      allowInput: true,
      altInput: true,
      altFormat: "d/m/Y",
    };

    this.fpStartDate = flatpickr(this.$refs.startDateInput, {
      ...config,
      onChange: (selectedDates, dateStr) => {
        this.filters.start_date = dateStr;
      },
    });

    this.fpEndDate = flatpickr(this.$refs.endDateInput, {
      ...config,
      onChange: (selectedDates, dateStr) => {
        this.filters.end_date = dateStr;
      },
    });
  },

  formatMoney(val) {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(val || 0);
  },

  formatDateBR(dateString) {
    if (!dateString) return "-";
    const [year, month, day] = dateString.split("-");
    return `${day}/${month}/${year}`;
  },

  async loadReportTable() {
    if (!this.filters.start_date || !this.filters.end_date) {
      return showAlert(
        "Por favor, selecione as datas Inicial e Final para a apuração.",
        "warning",
      );
    }

    try {
      const queryParams = new URLSearchParams(this.filters).toString();
      const response = await axios.get(
        `/api/client/reports/cash-flow?${queryParams}`,
      );

      this.reportList = response.data.data;
      this.summary = response.data.summary;
      this.hasSearched = true;

      if (this.dataTableInstance) this.dataTableInstance.destroy();

      const tbody = document.querySelector("#cashFlowReportTable tbody");
      if (!tbody) return;

      tbody.innerHTML = this.reportList
        .map((row) => {
          const balanceClass =
            row.daily_balance >= 0 ? "text-emerald-600" : "text-red-600";

          return `
          <tr class="hover:bg-slate-50 border-b border-gray-100">
            <td class="p-3 text-sm font-bold text-slate-800">${this.formatDateBR(row.date)}</td>
            <td class="p-3 text-sm font-mono text-emerald-600">${this.formatMoney(row.income_amount)}</td>
            <td class="p-3 text-sm font-mono text-red-500">${row.expense_amount > 0 ? "-" + this.formatMoney(row.expense_amount) : "-"}</td>
            <td class="p-3 text-sm font-mono font-bold ${balanceClass}">${this.formatMoney(row.daily_balance)}</td>
            <td class="p-3 text-sm text-center text-slate-500"><span class="bg-slate-100 px-2 py-1 rounded-md text-xs font-bold">${row.transaction_count}</span></td>
          </tr>
        `;
        })
        .join("");

      this.dataTableInstance = new DataTable("#cashFlowReportTable", {
        searchable: false,
        perPageSelect: false,
        labels: {
          noRows: "Nenhum movimento registrado neste período.",
          info: "Mostrando {start} a {end} de {rows} dias",
        },
      });
    } catch (error) {
      // Bloqueia caso o plano do usuário vença/seja alterado enquanto ele usa a tela
      if (error.response && error.response.status === 403) {
        this.hasAccessModule = false;
        return;
      }
      handleRequestError(error, "Erro ao processar fluxo de caixa.");
    }
  },

  applyFilters() {
    this.loadReportTable();
  },

  exportToCSV() {
    if (this.reportList.length === 0)
      return showAlert("Não há dados disponíveis para exportação.", "warning");

    let csvContent = "\uFEFF";
    csvContent += "Data;Receitas;Despesas;Saldo do Dia;Qtd Transações\n";

    this.reportList.forEach((row) => {
      const formattedExpense =
        row.expense_amount > 0 ? `-${row.expense_amount.toFixed(2)}` : "0.00";

      csvContent += `"${this.formatDateBR(row.date)}";"${row.income_amount.toFixed(2)}";"${formattedExpense}";"${row.daily_balance.toFixed(2)}";"${row.transaction_count}"\n`;
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `relatorio_fluxo_caixa_${this.filters.start_date}_a_${this.filters.end_date}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },

  async exportToPDF() {
    if (this.reportList.length === 0)
      return showAlert("Não há dados.", "warning");

    try {
      const response = await axios.post(
        "/api/client/reports/request-download-token",
      );
      const downloadToken = response.data.download_token;

      const queryParams = new URLSearchParams(this.filters);
      queryParams.append("dtoken", downloadToken);

      window.open(
        `/client/reports/cash-flow/pdf?${queryParams.toString()}`,
        "_blank",
      );
    } catch (error) {
      handleRequestError(
        error,
        "Erro ao solicitar autorização para impressão.",
      );
    }
  },
});
